import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { mediaAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { 
  FiUpload, FiImage, FiFolder, FiTrash2, FiEdit2, FiX, 
  FiCheck, FiGrid, FiList, FiSearch, FiPlus, FiCopy,
  FiVideo, FiFile, FiDownload, FiZap, FiAlertTriangle,
  FiStar, FiCamera, FiCalendar, FiEdit, FiLayout, FiMenu, FiUsers, FiExternalLink
} from 'react-icons/fi';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/apiUrl';

const API_URL = getApiUrl();

// Icon mapping for categories
const CATEGORY_ICONS = {
  folder: FiFolder,
  star: FiStar,
  camera: FiCamera,
  image: FiImage,
  calendar: FiCalendar,
  grid: FiGrid,
  edit: FiEdit,
  layout: FiLayout,
  menu: FiMenu,
  users: FiUsers,
};

const MediaLibraryPage = () => {
  const [media, setMedia] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    loadMedia();
    loadCategories();
  }, [selectedCategory]);

  const loadMedia = async () => {
    try {
      const response = await mediaAPI.getAll(selectedCategory);
      const mediaData = response.data?.media || response.data || [];
      setMedia(Array.isArray(mediaData) ? mediaData : []);
    } catch (error) {
      console.error('Error loading media:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await mediaAPI.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = [...e.dataTransfer.files];
    await uploadFiles(files);
  }, [selectedCategory]);

  const handleFileSelect = async (e) => {
    const files = [...e.target.files];
    await uploadFiles(files);
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    const category = selectedCategory || 'general';
    
    for (const file of files) {
      try {
        const response = await mediaAPI.upload(file, category, true);
        setMedia(prev => [response.data, ...prev]);
        toast.success(`${file.name} uploadé et optimisé !`);
      } catch (error) {
        toast.error(`Erreur: ${file.name}`);
      }
    }
    
    setUploading(false);
    setShowUploadZone(false);
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm('Supprimer ce média ?')) return;
    
    try {
      await mediaAPI.delete(mediaId);
      setMedia(prev => prev.filter(m => m.id !== mediaId));
      setSelectedMedia(null);
      toast.success('Média supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${API_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copiée !');
  };

  const createCategory = async () => {
    if (!newCategoryName) return;
    
    try {
      await mediaAPI.createCategory({
        name: newCategoryName,
        slug: newCategoryName.toLowerCase().replace(/[^a-z0-9]/g, '-')
      });
      await loadCategories();
      setShowNewCategoryModal(false);
      setNewCategoryName('');
      toast.success('Catégorie créée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const filteredMedia = media.filter(m => 
    !searchQuery || 
    m.original_filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.alt_text?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getMediaIcon = (contentType) => {
    if (contentType?.startsWith('video/')) return <FiVideo className="h-8 w-8" />;
    if (contentType?.includes('svg')) return <FiFile className="h-8 w-8" />;
    return <FiImage className="h-8 w-8" />;
  };

  return (
    <div className="space-y-6 bg-white rounded-xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Médiathèque</h1>
          <p className="text-slate-500">Gérez vos images et vidéos</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setShowNewCategoryModal(true)}>
            <FiFolder className="mr-2 h-4 w-4" /> Nouvelle catégorie
          </Button>
          <Button onClick={() => setShowUploadZone(true)}>
            <FiUpload className="mr-2 h-4 w-4" /> Ajouter des médias
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <Input 
            placeholder="Rechercher..." 
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <button
            className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${!selectedCategory ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            onClick={() => setSelectedCategory(null)}
          >
            <FiFolder className="w-4 h-4" />
            Tout ({media.length})
          </button>
          {categories.map(cat => {
            const IconComponent = CATEGORY_ICONS[cat.icon] || FiFolder;
            const isActive = selectedCategory === cat.slug;
            const catMediaCount = media.filter(m => m.category === cat.slug).length;
            return (
              <button
                key={cat.slug}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                  isActive 
                    ? 'text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                style={isActive ? { backgroundColor: cat.color || '#6366F1' } : {}}
                onClick={() => setSelectedCategory(cat.slug)}
                data-testid={`category-${cat.slug}`}
              >
                <IconComponent className="w-4 h-4" />
                {cat.name}
                {catMediaCount > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/20' : 'bg-slate-200'}`}>
                    {catMediaCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        
        <div className="flex items-center bg-slate-100 rounded-lg p-1">
          <button 
            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <FiGrid className="h-4 w-4" />
          </button>
          <button 
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <FiList className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Upload Zone */}
      <AnimatePresence>
        {showUploadZone && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-slate-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {uploading ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-2 border-indigo-600 border-t-transparent mb-4" />
                  <p className="text-slate-600">Upload et optimisation en cours...</p>
                </div>
              ) : (
                <>
                  <FiUpload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-lg font-medium text-slate-700 mb-2">
                    Glissez-déposez vos fichiers ici
                  </p>
                  <p className="text-slate-500 mb-4">ou</p>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept="image/*,video/mp4"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <span className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors">
                      Parcourir
                    </span>
                  </label>
                  <p className="text-sm text-slate-400 mt-4">
                    JPG, PNG, GIF, WebP, SVG, MP4 • Max 10MB • Optimisation automatique WebP
                  </p>
                </>
              )}
            </div>
            <div className="flex justify-end mt-2">
              <Button variant="ghost" size="sm" onClick={() => setShowUploadZone(false)}>
                Fermer
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Media Grid/List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
        </div>
      ) : filteredMedia.length === 0 ? (
        <div className="text-center py-20">
          <FiImage className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-700 mb-2">Aucun média</h3>
          <p className="text-slate-500 mb-4">Commencez par ajouter des images ou vidéos</p>
          <Button onClick={() => setShowUploadZone(true)}>
            <FiUpload className="mr-2" /> Ajouter des médias
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 gap-2">
          {filteredMedia.map((item, index) => {
            // Normalize media item fields
            const mediaUrl = item.url || item.file_url;
            const mimeType = item.mime_type || item.content_type || item.type || '';
            const isImage = mimeType.startsWith('image/') || item.file_type === 'image' || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(mediaUrl || '');
            const displayUrl = mediaUrl?.startsWith('http') ? mediaUrl : `${API_URL}${mediaUrl}`;
            
            return (
              <div
              key={item.id || item._id || index}
              className={`group relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                selectedMedia?.id === item.id ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-transparent hover:border-slate-300'
              }`}
              onClick={() => setSelectedMedia(item)}
            >
              {/* Image container */}
              <div className="aspect-square bg-slate-100">
                {isImage ? (
                  <img 
                    src={displayUrl}
                    alt={item.name || item.alt_text || item.file_name || item.original_filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { 
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="#f1f5f9" width="100" height="100"/><text x="50" y="45" text-anchor="middle" fill="#94a3b8" font-family="sans-serif" font-size="10">Image</text><text x="50" y="58" text-anchor="middle" fill="#94a3b8" font-family="sans-serif" font-size="10">non disponible</text></svg>');
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {getMediaIcon(item.content_type || item.type || item.file_type)}
                  </div>
                )}
              
              {/* External URL warning badge */}
              {mediaUrl?.startsWith('http') && !mediaUrl?.includes('emergentagent.com') && (
                <div className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1" title="URL externe - peut ne pas fonctionner">
                  <FiAlertTriangle className="h-3 w-3" />
                  Externe
                </div>
              )}
              
              {/* Category badge */}
              {item.category && (
                <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                  {item.category}
                </div>
              )}
              
              {/* Optimization badge - only show if no external URL warning */}
              {item.optimization && !item.optimization.error && (!mediaUrl?.startsWith('http') || mediaUrl?.includes('emergentagent.com')) && (
                <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                  <FiZap className="h-3 w-3" />
                  -{item.optimization.reduction_percent}%
                </div>
              )}
              
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button 
                  className="p-2 bg-white rounded-full hover:bg-slate-100"
                  onClick={(e) => { e.stopPropagation(); copyUrl(mediaUrl); }}
                >
                  <FiCopy className="h-4 w-4" />
                </button>
                <button 
                  className="p-2 bg-white rounded-full hover:bg-slate-100 text-red-500"
                  onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </div>
              
              {/* File name on hover */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-xs truncate">{item.file_name || item.original_filename}</p>
              </div>
              </div>
              
              {/* Usage info below image */}
              {item.used_in && item.used_in.length > 0 && (
                <div className="p-1 bg-slate-50 border-t text-[10px] text-slate-500 truncate">
                  📄 {item.used_in.slice(0, 2).join(', ')}{item.used_in.length > 2 ? ` +${item.used_in.length - 2}` : ''}
                </div>
              )}
            </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredMedia.map(item => {
            // Normalize fields - check for various field names
            const mediaUrl = item.url || item.file_url;
            const mimeType = item.mime_type || item.content_type || item.type || '';
            const isImage = mimeType.startsWith('image/') || item.file_type === 'image' || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(mediaUrl || '');
            const displayUrl = mediaUrl?.startsWith('http') ? mediaUrl : `${API_URL}${mediaUrl}`;
            const fileName = item.original_filename || item.file_name || item.name || 'Fichier';
            const fileSize = item.size || item.file_size || 0;
            
            return (
              <div
                key={item.id}
                className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer ${
                  selectedMedia?.id === item.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => setSelectedMedia(item)}
              >
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                  {isImage && mediaUrl ? (
                    <img 
                      src={displayUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`w-full h-full flex items-center justify-center ${isImage && mediaUrl ? 'hidden' : ''}`}>
                    {getMediaIcon(item.content_type || item.type)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{fileName}</p>
                  <p className="text-sm text-slate-500">
                    {fileSize > 0 ? formatSize(fileSize) : 'Externe'} • {item.category || 'Général'} • {new Date(item.created_at || item.uploaded_at || Date.now()).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                {item.optimization && !item.optimization.error && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    -{item.optimization.reduction_percent}%
                  </span>
                )}
                <div className="flex gap-2">
                  <button className="p-2 hover:bg-slate-100 rounded" onClick={(e) => { e.stopPropagation(); copyUrl(mediaUrl); }}>
                    <FiCopy className="h-4 w-4" />
                  </button>
                  <button className="p-2 hover:bg-red-100 rounded text-red-500" onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}>
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Media Detail Panel */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedMedia(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex"
              onClick={e => e.stopPropagation()}
            >
              {/* Preview */}
              <div className="flex-1 bg-slate-900 flex items-center justify-center p-4">
                {(selectedMedia.mime_type?.startsWith('image/') || selectedMedia.content_type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(selectedMedia.url || '')) ? (
                  <img 
                    src={selectedMedia.url?.startsWith('http') ? selectedMedia.url : `${API_URL}${selectedMedia.url}`}
                    alt={selectedMedia.alt_text || selectedMedia.original_filename || ''}
                    className="max-w-full max-h-[70vh] object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="text-white text-center">
                    {getMediaIcon(selectedMedia.mime_type || selectedMedia.content_type)}
                    <p className="mt-4">{selectedMedia.original_filename || selectedMedia.file_name}</p>
                  </div>
                )}
              </div>
              
              {/* Details */}
              <div className="w-80 p-6 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold">Détails</h3>
                  <button onClick={() => setSelectedMedia(null)}>
                    <FiX className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-slate-500">Nom du fichier</Label>
                    <p className="font-medium truncate text-sm">{selectedMedia.original_name || selectedMedia.original_filename || selectedMedia.file_name || 'Fichier sans nom'}</p>
                  </div>
                  
                  {/* Editable SEO Name */}
                  <div>
                    <Label className="text-xs text-slate-500">Nom SEO (pour le référencement)</Label>
                    <Input 
                      value={selectedMedia.name || selectedMedia.seo_filename || ''}
                      onChange={(e) => {
                        setSelectedMedia(prev => ({ ...prev, name: e.target.value }));
                      }}
                      onBlur={async () => {
                        if (selectedMedia.name) {
                          await mediaAPI.update(selectedMedia.id, { name: selectedMedia.name, seo_filename: selectedMedia.name });
                          loadMedia();
                          toast.success('Nom SEO mis à jour');
                        }
                      }}
                      placeholder="nom-optimise-pour-seo"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-400 mt-1">Utilisez des tirets, pas d'espaces ni d'accents</p>
                  </div>
                  
                  {/* Title */}
                  <div>
                    <Label className="text-xs text-slate-500">Titre (attribut title)</Label>
                    <Input 
                      value={selectedMedia.title || ''}
                      onChange={(e) => {
                        setSelectedMedia(prev => ({ ...prev, title: e.target.value }));
                      }}
                      onBlur={async () => {
                        await mediaAPI.update(selectedMedia.id, { title: selectedMedia.title });
                        toast.success('Titre mis à jour');
                      }}
                      placeholder="Titre descriptif de l'image"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-500">Taille</Label>
                    <p className="font-medium">{formatSize(selectedMedia.size || selectedMedia.file_size || 0)}</p>
                  </div>
                  
                  {/* Compression / Optimization Info */}
                  {selectedMedia.optimization ? (
                    <div className="bg-green-50 p-3 rounded-lg">
                      <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                        <FiZap /> Optimisé
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        {formatSize(selectedMedia.optimization.original_size)} → {formatSize(selectedMedia.optimization.optimized_size)}
                      </p>
                      <p className="text-xs text-green-600">
                        Réduction: {selectedMedia.optimization.reduction_percent}%
                      </p>
                      {selectedMedia.optimization.width && (
                        <p className="text-xs text-green-600">
                          Dimensions: {selectedMedia.optimization.width} x {selectedMedia.optimization.height}
                        </p>
                      )}
                    </div>
                  ) : selectedMedia.original_size ? (
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                        <FiZap /> Compression
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Original: {formatSize(selectedMedia.original_size)}
                      </p>
                      <p className="text-xs text-blue-600">
                        Compressé: {formatSize(selectedMedia.size || selectedMedia.file_size || 0)}
                      </p>
                      {selectedMedia.original_size && selectedMedia.size && (
                        <p className="text-xs text-blue-600 font-medium">
                          Réduction: {Math.round((1 - (selectedMedia.size / selectedMedia.original_size)) * 100)}%
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">
                        Taille du fichier: {formatSize(selectedMedia.size || selectedMedia.file_size || 0)}
                      </p>
                      {selectedMedia.mime_type && (
                        <p className="text-xs text-slate-500">
                          Type: {selectedMedia.mime_type}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Pages where the image is used */}
                  <div>
                    <Label className="text-xs text-slate-500">Utilisée sur</Label>
                    {selectedMedia.used_in && selectedMedia.used_in.length > 0 ? (
                      <div className="mt-1 space-y-1">
                        {selectedMedia.used_in.map((page, idx) => (
                          <a
                            key={idx}
                            href={`/${page}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 hover:underline"
                          >
                            <FiExternalLink className="w-3 h-3" />
                            {page}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 mt-1">Non utilisée actuellement</p>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-500">Catégorie</Label>
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      {categories.map(cat => {
                        const IconComponent = CATEGORY_ICONS[cat.icon] || FiFolder;
                        const isSelected = selectedMedia.category === cat.slug;
                        return (
                          <button
                            key={cat.slug}
                            type="button"
                            className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                              isSelected
                                ? 'text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                            style={isSelected ? { backgroundColor: cat.color || '#6366F1' } : {}}
                            onClick={async () => {
                              await mediaAPI.update(selectedMedia.id, { category: cat.slug });
                              setSelectedMedia(prev => ({ ...prev, category: cat.slug }));
                              loadMedia();
                              toast.success('Catégorie mise à jour');
                            }}
                          >
                            <IconComponent className="w-3 h-3" />
                            {cat.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Old select fallback - hidden but kept for compatibility */}
                  <select 
                    className="hidden"
                    value={selectedMedia.category}
                    onChange={async (e) => {
                      await mediaAPI.update(selectedMedia.id, { category: e.target.value });
                      setSelectedMedia(prev => ({ ...prev, category: e.target.value }));
                      loadMedia();
                      toast.success('Catégorie mise à jour');
                    }}
                  >
                      {categories.map(cat => (
                        <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                      ))}
                  </select>
                  
                  <div>
                    <Label className="text-xs text-slate-500">Texte alternatif (SEO)</Label>
                    <Input 
                      value={selectedMedia.alt_text || ''}
                      onChange={async (e) => {
                        setSelectedMedia(prev => ({ ...prev, alt_text: e.target.value }));
                      }}
                      onBlur={async () => {
                        await mediaAPI.update(selectedMedia.id, { alt_text: selectedMedia.alt_text });
                        toast.success('Texte alternatif mis à jour');
                      }}
                      placeholder="Description de l'image pour le SEO"
                    />
                    <p className="text-xs text-slate-400 mt-1">Important pour l'accessibilité et le référencement</p>
                  </div>
                  
                  {/* Description SEO */}
                  <div>
                    <Label className="text-xs text-slate-500">Description (meta description)</Label>
                    <textarea 
                      value={selectedMedia.description || ''}
                      onChange={(e) => {
                        setSelectedMedia(prev => ({ ...prev, description: e.target.value }));
                      }}
                      onBlur={async () => {
                        await mediaAPI.update(selectedMedia.id, { description: selectedMedia.description });
                        toast.success('Description mise à jour');
                      }}
                      placeholder="Description détaillée pour le référencement..."
                      className="w-full border rounded-lg p-2 mt-1 text-sm resize-none"
                      rows={3}
                    />
                  </div>
                  
                  {/* SEO Optimization Section */}
                  <div className="bg-indigo-50 p-4 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-indigo-900 flex items-center gap-2">
                        <FiZap className="h-4 w-4" />
                        Optimisation SEO
                      </h4>
                      {selectedMedia.seo_optimized && (
                        <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Optimisé</span>
                      )}
                    </div>
                    
                    {selectedMedia.seo_filename && (
                      <div>
                        <Label className="text-xs text-indigo-600">Nom SEO</Label>
                        <p className="text-sm font-medium">{selectedMedia.seo_filename}</p>
                      </div>
                    )}
                    
                    {selectedMedia.seo_keywords?.length > 0 && (
                      <div>
                        <Label className="text-xs text-indigo-600">Mots-clés</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedMedia.seo_keywords.map((kw, i) => (
                            <span key={i} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{kw}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <Button 
                      variant="default" 
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      onClick={async () => {
                        toast.loading('Optimisation IA en cours...', { id: 'seo-opt' });
                        try {
                          const response = await mediaAPI.optimizeSeo(selectedMedia.id);
                          toast.success('Image optimisée pour le SEO !', { id: 'seo-opt' });
                          setSelectedMedia(prev => ({
                            ...prev,
                            seo_filename: response.data.optimized.filename,
                            alt_text: response.data.optimized.alt_text,
                            seo_keywords: response.data.optimized.keywords,
                            seo_optimized: true
                          }));
                          loadMedia();
                        } catch (error) {
                          toast.error('Erreur lors de l\'optimisation', { id: 'seo-opt' });
                        }
                      }}
                    >
                      <FiZap className="mr-2" />
                      {selectedMedia.seo_optimized ? 'Ré-optimiser avec l&apos;IA' : 'Optimiser avec l&apos;IA'}
                    </Button>
                    <p className="text-xs text-indigo-600/70 text-center">
                      L&apos;IA va générer un nom de fichier et un texte alternatif optimisés pour le référencement
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-500">URL</Label>
                    <div className="flex gap-2 mt-1">
                      <Input 
                        value={`${API_URL}${selectedMedia.url}`}
                        readOnly
                        className="text-xs"
                      />
                      <Button variant="outline" size="sm" onClick={() => copyUrl(selectedMedia.url)}>
                        <FiCopy />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-500">Ajouté le</Label>
                    <p className="text-sm">{new Date(selectedMedia.created_at).toLocaleString('fr-FR')}</p>
                  </div>
                  
                  <hr />
                  
                  <Button variant="destructive" className="w-full" onClick={() => deleteMedia(selectedMedia.id)}>
                    <FiTrash2 className="mr-2" /> Supprimer
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Category Modal */}
      <AnimatePresence>
        {showNewCategoryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowNewCategoryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl p-6 max-w-md w-full mx-4"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">Nouvelle catégorie</h3>
              <div className="space-y-4">
                <div>
                  <Label>Nom de la catégorie</Label>
                  <Input 
                    value={newCategoryName}
                    onChange={e => setNewCategoryName(e.target.value)}
                    placeholder="Ex: Événements"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button variant="outline" className="flex-1" onClick={() => setShowNewCategoryModal(false)}>
                  Annuler
                </Button>
                <Button className="flex-1" onClick={createCategory}>
                  Créer
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaLibraryPage;
